import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { Modal, Button, toast } from "@/components/ui";
import { queryKeys } from "@/constants/queryKeys";
import {
  fetchChatConversations,
  forwardChatMessage,
} from "@/services/api/chat";
import { useMessagesStore } from "../store/useMessagesStore";
import { ConversationAvatar } from "./ConversationAvatar";
import { cn } from "@/lib/cn";

export function ForwardMessageModal() {
  const queryClient = useQueryClient();
  const forwardMessage = useMessagesStore((s) => s.forwardMessage);
  const setForwardMessage = useMessagesStore((s) => s.setForwardMessage);
  const setActiveConversationId = useMessagesStore((s) => s.setActiveConversationId);
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const open = Boolean(forwardMessage);

  const { data: conversations = [], isLoading } = useQuery({
    queryKey: queryKeys.chatConversations(q),
    queryFn: () => fetchChatConversations({ q: q || undefined }),
    enabled: open,
  });

  const targets = useMemo(
    () =>
      conversations.filter(
        (item) => item.id !== forwardMessage?.conversation,
      ),
    [conversations, forwardMessage?.conversation],
  );

  const mutation = useMutation({
    mutationFn: async () => {
      if (!forwardMessage || !selectedId) {
        throw new Error("Pick a chat to forward to.");
      }
      const fromName =
        forwardMessage.sender?.name ||
        forwardMessage.sender?.username ||
        "Chat";
      return forwardChatMessage({
        targetConversationId: selectedId,
        body: forwardMessage.body || forwardMessage.attachment_name || "[Attachment]",
        forwarded_from_name: fromName,
        forwarded_from_message_id: forwardMessage.id,
      });
    },
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: ["chat-conversations"] });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.chatTimeline(created.conversation),
      });
      setForwardMessage(null);
      setSelectedId(null);
      setQ("");
      setActiveConversationId(created.conversation);
      toast.success("Message forwarded");
    },
    onError: () => {
      toast.error("Could not forward", "Try again in a moment.");
    },
  });

  const close = () => {
    if (mutation.isPending) return;
    setForwardMessage(null);
    setSelectedId(null);
    setQ("");
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title="Forward message"
      className="max-w-md"
      footer={
        <>
          <Button variant="ghost" onClick={close} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => mutation.mutate()}
            loading={mutation.isPending}
            disabled={!selectedId}
          >
            Forward
          </Button>
        </>
      }
    >
      {forwardMessage ? (
        <div className="mb-3 rounded-[8px] border border-border bg-surface-muted px-3 py-2">
          <p className="text-[11px] font-semibold tracking-wide text-fg-subtle uppercase">
            Forwarded message
          </p>
          <p className="mt-1 line-clamp-3 text-sm text-fg">
            {forwardMessage.body || forwardMessage.attachment_name || "Attachment"}
          </p>
        </div>
      ) : null}

      <div className="relative mb-3">
        <Search className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-fg-subtle" />
        <input
          value={q}
          onChange={(event) => setQ(event.target.value)}
          placeholder="Search chats…"
          className="h-10 w-full rounded-[6px] border border-border bg-surface-muted pr-3 pl-9 text-sm outline-none"
        />
      </div>

      <div className="max-h-64 space-y-1 overflow-y-auto">
        {isLoading ? (
          <p className="px-2 py-6 text-center text-sm text-fg-muted">Loading…</p>
        ) : targets.length === 0 ? (
          <p className="px-2 py-6 text-center text-sm text-fg-muted">No chats found</p>
        ) : (
          targets.map((item) => {
            const active = selectedId === item.id;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => setSelectedId(item.id)}
                className={cn(
                  "flex w-full items-center gap-3 rounded-[6px] px-2 py-2 text-left transition-colors",
                  active ? "bg-papaya-leaf/15" : "hover:bg-surface-muted",
                )}
              >
                <ConversationAvatar conversation={item} size="sm" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold text-fg">
                    {item.name}
                  </span>
                  <span className="block truncate text-xs text-fg-muted">
                    {item.preview || (item.type === "group" ? "Group" : "Direct")}
                  </span>
                </span>
              </button>
            );
          })
        )}
      </div>
    </Modal>
  );
}
